import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { AlertTriangle, Archive, RefreshCw, SlidersHorizontal } from 'lucide-react';

// Time estimates based on difficulty (in seconds for progress calculation)
const EXPECTED_DURATION = {
  easy: 300,    // 5 minutes
  medium: 420,  // 7 minutes
  hard: 600     // 10 minutes
};

export const CaseGenerationLoading = ({
  theme,
  difficulty = 'medium',
  progress: sseProgress,
  isFailed = false,
  errorMessage,
  jobId,
  retryable = true,
  lifecycleStatus = null,
  onRetry,
  onChangeDifficulty,
  onViewArchive
}) => {
  const [dots, setDots] = useState('.');
  const [messageIndex, setMessageIndex] = useState(0);
  const [fallbackProgress, setFallbackProgress] = useState(0);

  // Use SSE progress when available, otherwise use fallback linear animation
  const progress = sseProgress?.progress ?? fallbackProgress;

  // Time estimates for display
  const timeEstimates = {
    easy: '4-6 minutes',
    medium: '6-8 minutes',
    hard: '8-12 minutes'
  };

  // Rotating messages for each theme
  const loadingMessages = {
    noir: [
      'Building the case file...',
      'Investigating the crime scene...',
      'Gathering witness statements...',
      'Processing evidence...',
      'Developing photographs...',
      'Compiling suspect profiles...'
    ],
    '90s_nyc': [
      'Setting up the case...',
      'Checking out the crime scene...',
      'Rounding up witnesses...',
      'Collecting evidence...',
      'Printing photos at the lab...',
      'Running background checks...'
    ],
    miami_vice: [
      'Opening the case...',
      'Surveilling the scene...',
      'Interviewing witnesses...',
      'Gathering intel...',
      'Processing surveillance photos...',
      'Building suspect dossiers...'
    ]
  };

  const themeConfig = {
    noir: {
      bg: 'from-slate-900 to-slate-800',
      accent: '#DC143C'
    },
    '90s_nyc': {
      bg: 'from-orange-900 to-blue-900',
      accent: '#FF6B35'
    },
    miami_vice: {
      bg: 'from-purple-900 to-cyan-900',
      accent: '#FF1493'
    },
  };

  const fallbackMessages = loadingMessages[theme] || loadingMessages.noir;
  const config = themeConfig[theme] || themeConfig.noir;
  const isUnknownMaybeRunning = lifecycleStatus === 'unknown_maybe_running';

  // Use SSE message when available, otherwise use rotating fallback messages
  const currentMessage = sseProgress?.message || fallbackMessages[messageIndex];

  useEffect(() => {
    // Animate dots
    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '.' : prev + '.');
    }, 500);

    // Cycle through messages every 4 seconds
    const messageInterval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % fallbackMessages.length);
    }, 4000);

    // Linear progress animation - fills to ~95% over expected duration
    const duration = EXPECTED_DURATION[difficulty] || EXPECTED_DURATION.medium;
    const updateInterval = 1000; // Update every second
    const incrementPerSecond = 95 / duration; // Reach 95% by expected end time

    const progressInterval = setInterval(() => {
      setFallbackProgress(prev => {
        const next = prev + incrementPerSecond;
        // Cap at 95% - the actual completion will jump to 100%
        return next >= 95 ? 95 : next;
      });
    }, updateInterval);

    return () => {
      clearInterval(dotsInterval);
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [fallbackMessages.length, difficulty]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #c19a6b 0%, #b8936f 25%, #c19a6b 50%, #ab8562 75%, #c19a6b 100%)',
        backgroundSize: '400% 400%',
      }}
    >
      {/* Cork texture overlay */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 2px,
              rgba(139, 90, 43, 0.1) 2px,
              rgba(139, 90, 43, 0.1) 4px
            ),
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 2px,
              rgba(139, 90, 43, 0.1) 2px,
              rgba(139, 90, 43, 0.1) 4px
            )
          `,
          backgroundSize: '20px 20px',
        }}
      ></div>

      {/* Decorative items */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-full max-h-[700px] pointer-events-none">
        {/* Large Evidence Polaroid - Top Left */}
        <div className="absolute -top-24 -left-24 w-56 h-64 bg-white shadow-2xl rotate-[-12deg] z-10 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1575936123452-b67c3203c357?w=400&h=400&fit=crop"
            alt="Evidence"
            className="w-full h-52 object-cover"
          />
          <p className="text-base text-center font-handwriting pt-2 text-gray-700 font-bold">EVIDENCE #601</p>
        </div>

        {/* Large Yellow Post-it Note - Top Right */}
        <div className="absolute -top-32 -right-20 w-64 h-64 bg-yellow-300 shadow-2xl rotate-[8deg] p-6 z-10">
          <p className="text-lg font-handwriting text-gray-800 leading-relaxed">
            3 ACTIVE CASES<br/>
            ALL UNSOLVED.<br/>
            TIME TO CRACK<br/>
            THEM.
          </p>
        </div>

        {/* Large Red Evidence Folder - Bottom Left */}
        <div className="absolute -bottom-24 -left-20 w-72 h-80 bg-red-100 border-4 border-red-700 shadow-2xl rotate-[-5deg] p-4 z-10">
          <div className="border-4 border-dashed border-red-700 h-full flex flex-col items-center justify-center gap-3">
            <p className="text-2xl font-bold text-red-700 text-center tracking-wider">CASE FILE</p>
            <div className="w-16 h-16 border-4 border-red-700 rounded-full flex items-center justify-center">
              <p className="text-red-700 font-bold text-xs">DO NOT<br/>REMOVE</p>
            </div>
            <p className="text-sm font-bold text-red-700">CLASSIFIED</p>
          </div>
        </div>

        {/* Large Clue Polaroid - Bottom Right */}
        <div className="absolute -bottom-20 -right-24 w-52 h-60 bg-white shadow-2xl rotate-[10deg] z-10 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=400&fit=crop"
            alt="Clue"
            className="w-full h-48 object-cover"
          />
          <p className="text-base text-center font-handwriting pt-2 text-gray-700 font-bold">Check Timelines</p>
        </div>

        {/* Large Pink Post-it Note - Left Side */}
        <div className="absolute top-1/4 -left-28 w-56 h-56 bg-pink-200 shadow-2xl rotate-[-15deg] p-5 z-10">
          <p className="text-base font-handwriting text-gray-800 leading-relaxed">
            CHECK MOTIVE<br/>
            TIMELINE<br/>
            TESTIMONY!!
          </p>
        </div>

        {/* Confidential Files Folder - Right Side */}
        <div className="absolute bottom-1/3 -right-20 w-60 h-48 bg-yellow-100 shadow-2xl rotate-[12deg] z-10 overflow-hidden border-2 border-amber-700">
          <div className="bg-amber-700 text-white text-center py-2 text-xs font-bold tracking-widest">CONFIDENTIAL FILES</div>
          <div className="p-4 space-y-1">
            <div className="h-2 bg-amber-700/30 w-full"></div>
            <div className="h-2 bg-amber-700/30 w-5/6"></div>
            <div className="h-2 bg-amber-700/30 w-4/5"></div>
            <div className="h-2 bg-amber-700/30 w-full"></div>
          </div>
        </div>

        {/* Push Pins scattered */}
        <div className="absolute top-10 left-1/4 w-6 h-6 rounded-full bg-red-600 shadow-lg z-20"></div>
        <div className="absolute top-20 right-1/3 w-5 h-5 rounded-full bg-blue-500 shadow-lg z-20"></div>
        <div className="absolute bottom-32 left-1/3 w-6 h-6 rounded-full bg-yellow-400 shadow-lg z-20"></div>

        {/* Paper clips */}
        <div className="absolute top-1/3 right-1/4 w-12 h-20 border-4 border-gray-400 rounded-full rotate-45 z-[5]"></div>
        <div className="absolute bottom-1/4 left-1/4 w-10 h-16 border-4 border-gray-500 rounded-full rotate-[-30deg] z-[5]"></div>
      </div>

      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gray-800 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gray-800 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <Card className="max-w-2xl w-full bg-black/60 backdrop-blur-xl border-white/20 p-12 relative z-10">
        <div className="space-y-8 text-center">
          {isFailed ? (
            <>
              <div className="flex justify-center">
                <div className="rounded-full border border-red-300/40 bg-red-500/15 p-4 text-red-200">
                  <AlertTriangle className="h-8 w-8" aria-hidden="true" />
                </div>
              </div>

              <div>
                <h1 className="text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {isUnknownMaybeRunning ? 'Generation Status Unknown' : 'Generation Failed'}
                </h1>
                <p className="text-gray-200 text-lg">
                  {errorMessage || 'Case generation failed. Please try again.'}
                </p>
              </div>

              {jobId && (
                <div className="rounded-md border border-white/10 bg-white/5 px-4 py-3 text-left">
                  <p className="text-xs uppercase tracking-wider text-gray-500">Reference ID</p>
                  <p className="mt-1 break-all font-mono text-sm text-gray-200">{jobId}</p>
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                {retryable && (
                  <Button
                    type="button"
                    onClick={onRetry}
                    disabled={!onRetry}
                    className="bg-white text-gray-950 hover:bg-gray-200"
                  >
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                    Try Again
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={onChangeDifficulty}
                  disabled={!onChangeDifficulty}
                  className="border-white/20 text-white"
                >
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                  Change Difficulty
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onViewArchive}
                  disabled={!onViewArchive}
                  className="border-white/20 text-white"
                >
                  <Archive className="h-4 w-4" aria-hidden="true" />
                  View Archive
                </Button>
              </div>

              {!retryable && (
                <p className="text-sm text-gray-400">
                  {isUnknownMaybeRunning
                    ? 'This job may still finish in the background. Check your archive, or choose a difficulty to start fresh.'
                    : 'This generation job cannot be retried directly. Choose a new difficulty or check your archive.'}
                </p>
              )}
            </>
          ) : (
            <>
              {/* Title */}
              <div>
                <h1 className="text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Generating Case
                </h1>
                <p className="text-gray-300 text-lg">
                  Your investigation is being prepared{dots}
                </p>
              </div>

              {/* Animated progress indicator */}
              <div className="space-y-6">
                {/* Linear progress bar */}
                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-linear"
                    style={{
                      width: `${progress}%`,
                      backgroundColor: config.accent,
                    }}
                  />
                </div>

                {/* Progress percentage */}
                <p className="text-gray-400 text-sm">
                  {Math.round(progress)}% complete
                </p>

                {/* Rotating message */}
                <div className="h-12 flex items-center justify-center">
                  <p
                    className="text-xl font-semibold transition-opacity duration-500"
                    style={{ color: config.accent }}
                  >
                    {currentMessage}
                  </p>
                </div>
              </div>

              {/* Info text */}
              <div className="pt-6 border-t border-white/10">
                <p className="text-gray-400 text-sm">
                  We're crafting a unique murder mystery just for you.
                  <br />
                  <span className="text-gray-500">
                    Estimated time: {timeEstimates[difficulty] || timeEstimates.medium}
                  </span>
                </p>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
